const REQUIRED_KEYPOINTS = ['shoulder', 'elbow', 'wrist', 'hip', 'knee', 'ankle'];

export function angleDegrees(first, center, last) {
  if (!first || !center || !last) return null;
  const ax = first.x - center.x;
  const ay = first.y - center.y;
  const bx = last.x - center.x;
  const by = last.y - center.y;
  const denominator = Math.hypot(ax, ay) * Math.hypot(bx, by);
  if (!denominator) return null;
  const cosine = Math.max(-1, Math.min(1, ((ax * bx) + (ay * by)) / denominator));
  return Math.acos(cosine) * 180 / Math.PI;
}

function confidenceForSide(side) {
  if (!side) return 0;
  return Math.min(...REQUIRED_KEYPOINTS.map(name => Number(side[name]?.confidence ?? 0)));
}

export function chooseTrackedSide(observation, metadata) {
  const minimum = metadata?.tracking?.tracking_confidence_min ?? 0.5;
  const candidates = ['left', 'right']
    .map(name => ({ name, confidence: confidenceForSide(observation?.sides?.[name]) }))
    .filter(candidate => candidate.confidence >= minimum)
    .sort((a, b) => b.confidence - a.confidence);
  return candidates[0]?.name || null;
}

function normalizedDistanceToLine(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!length) return null;
  const distance = Math.abs((dy * point.x) - (dx * point.y) + (end.x * start.y) - (end.y * start.x)) / length;
  return distance / length;
}

export function signalsForSide(side) {
  if (!side) return null;
  const elbowAngle = angleDegrees(side.shoulder, side.elbow, side.wrist);
  const hipError = normalizedDistanceToLine(side.hip, side.shoulder, side.ankle);
  const kneeError = normalizedDistanceToLine(side.knee, side.shoulder, side.ankle);
  if (![elbowAngle, hipError, kneeError].every(Number.isFinite)) return null;
  return {
    elbow_angle: elbowAngle,
    body_line_error: Math.max(hipError, kneeError),
  };
}

function matches(value, operator, threshold) {
  if (!Number.isFinite(value)) return false;
  if (operator === 'gte') return value >= threshold;
  if (operator === 'lte') return value <= threshold;
  if (operator === 'gt') return value > threshold;
  if (operator === 'lt') return value < threshold;
  return false;
}

export function createPushupRepCounter(metadata) {
  const tracking = metadata?.tracking || {};
  const counting = metadata?.rep_counting || {};
  const top = metadata?.key_moments?.top || {};
  const bottom = metadata?.key_moments?.bottom || {};
  const formRules = metadata?.form_feedback?.rules || [];
  const state = {
    count: 0,
    phase: 'awaiting_top',
    selectedSide: null,
    lockedSide: null,
    lastTrackedAt: null,
    candidate: null,
    candidateSince: null,
    repStartedAt: null,
    minimumElbowAngle: Infinity,
    maximumElbowAngle: -Infinity,
    lastCountedAt: -Infinity,
    formSince: {},
    warnings: new Set(),
    completedReps: [],
    lastRep: null,
  };

  const publicState = () => ({
    count: state.count,
    phase: state.phase,
    selectedSide: state.selectedSide,
    tracking: state.lastTrackedAt != null,
    lastRep: state.lastRep,
    completedReps: [...state.completedReps],
  });

  function clearCandidate() {
    state.candidate = null;
    state.candidateSince = null;
  }

  function resetPartial() {
    state.phase = 'awaiting_top';
    state.lockedSide = null;
    state.repStartedAt = null;
    state.minimumElbowAngle = Infinity;
    state.maximumElbowAngle = -Infinity;
    state.formSince = {};
    state.warnings = new Set();
    clearCandidate();
  }

  function confirmed(moment, timestamp, confirmationMs) {
    if (state.candidate !== moment) {
      state.candidate = moment;
      state.candidateSince = timestamp;
      return false;
    }
    if (timestamp - state.candidateSince >= confirmationMs) {
      clearCandidate();
      return true;
    }
    return false;
  }

  function updateForm(signals, timestamp) {
    formRules.forEach(rule => {
      if (matches(signals[rule.signal], rule.operator, rule.value)) {
        if (state.formSince[rule.code] == null) state.formSince[rule.code] = timestamp;
        if (timestamp - state.formSince[rule.code] >= (rule.hold_ms || 0)) state.warnings.add(rule.code);
      } else {
        delete state.formSince[rule.code];
      }
    });
  }

  function momentMatches(moment, signals) {
    return (moment.all || []).every(rule => matches(signals[rule.signal], rule.operator, rule.value));
  }

  function update(observation) {
    const timestamp = Number(observation?.timestamp);
    if (!Number.isFinite(timestamp)) return publicState();

    let sideName = state.lockedSide || chooseTrackedSide(observation, metadata);
    let confidence = confidenceForSide(observation?.sides?.[sideName]);
    const trackingMinimum = state.lockedSide
      ? (tracking.tracking_confidence_min ?? 0.5)
      : (tracking.transition_confidence_min ?? 0.65);

    if (!sideName || confidence < trackingMinimum) {
      if (state.lastTrackedAt != null && timestamp - state.lastTrackedAt > (tracking.reset_partial_rep_after_dropout_ms ?? 500)) {
        resetPartial();
        sideName = chooseTrackedSide(observation, metadata);
        state.selectedSide = sideName;
      }
      return publicState();
    }

    state.selectedSide = sideName;
    state.lastTrackedAt = timestamp;
    const signals = signalsForSide(observation.sides[sideName]);
    if (!signals) return publicState();
    state.minimumElbowAngle = Math.min(state.minimumElbowAngle, signals.elbow_angle);
    state.maximumElbowAngle = Math.max(state.maximumElbowAngle, signals.elbow_angle);
    updateForm(signals, timestamp);

    const isTop = momentMatches(top, signals);
    const isBottom = momentMatches(bottom, signals);

    if (state.phase === 'awaiting_top') {
      const topCandidateStartedAt = state.candidate === 'top' ? state.candidateSince : timestamp;
      if (isTop && confirmed('top', timestamp, top.confirmation_ms || 0)) {
        state.phase = 'top';
        state.repStartedAt = topCandidateStartedAt;
        state.minimumElbowAngle = signals.elbow_angle;
        state.maximumElbowAngle = signals.elbow_angle;
      } else if (!isTop) {
        clearCandidate();
      }
      return publicState();
    }

    if (state.phase === 'top') {
      if (signals.elbow_angle < counting.top_exit_elbow_deg) {
        state.phase = 'descending';
        state.lockedSide = sideName;
        clearCandidate();
      }
      return publicState();
    }

    if (state.phase === 'descending') {
      if (isBottom && confirmed('bottom', timestamp, bottom.confirmation_ms || 0)) {
        state.phase = 'bottom';
      } else if (!isBottom) {
        clearCandidate();
      }
      return publicState();
    }

    if (state.phase === 'bottom') {
      if (signals.elbow_angle > counting.bottom_exit_elbow_deg) {
        state.phase = 'ascending';
        clearCandidate();
      }
      return publicState();
    }

    if (state.phase === 'ascending') {
      if (isTop && confirmed('final_top', timestamp, top.confirmation_ms || 0)) {
        const durationMs = timestamp - state.repStartedAt;
        const rom = state.maximumElbowAngle - state.minimumElbowAngle;
        const plausible = durationMs >= counting.minimum_rep_ms
          && durationMs <= counting.maximum_rep_ms
          && rom >= counting.minimum_elbow_rom_deg
          && timestamp - state.lastCountedAt >= (counting.post_count_debounce_ms || 0);
        if (plausible) {
          state.count += 1;
          state.lastCountedAt = timestamp;
          state.lastRep = { durationMs, rangeOfMotionDegrees: rom, warnings: [...state.warnings] };
          state.completedReps.push(state.lastRep);
        }
        state.phase = 'top';
        state.lockedSide = null;
        state.repStartedAt = null;
        state.minimumElbowAngle = signals.elbow_angle;
        state.maximumElbowAngle = signals.elbow_angle;
        state.formSince = {};
        state.warnings = new Set();
      } else if (!isTop) {
        clearCandidate();
      }
    }
    return publicState();
  }

  return { update, getState: publicState, reset: resetPartial };
}
